<template>
  <v-card width="100%" :loading="$apollo.loading" flat tile>
    <v-card-actions class="pa-4">
      <upload-files
        :uploadPath="currentPath"
        @uploaded="refetch"
        small
        depressed
        color="primary"
      ></upload-files>
      <create-directory
        :path="currentPath"
        @created="refetch"
        small
        depressed
        color="primary"
      ></create-directory>

      <v-spacer></v-spacer>

      <v-btn icon small v-if="isDialog" @click="close">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-actions>

    <v-card-actions class="grey lighten-2">
      <v-breadcrumbs :items="breadcrumbItems" divider="/" class="pa-0">
        <template v-slot:item="bread">
          <v-breadcrumbs-item :disabled="false" x-small text>
            <v-btn
              small
              text
              :ripple="false"
              @click.native="navigatePath(bread.item.index)"
              >{{ bread.item.text }}</v-btn
            >
          </v-breadcrumbs-item>
        </template>
      </v-breadcrumbs>
    </v-card-actions>

    <!-- TODO - Fix media grid -->
    <!-- TODO - Optimize media by using thumbnails on images -->
    <v-card-text v-if="!$apollo.loading" class="pb-8">
      <v-row dense>
        <v-col sm="9" md="10">
          <v-row dense>
            <v-col sm="1" v-if="paths.length">
              <v-card link @dblclick="backDirectory()" height="100%">
                <v-row justify="center" no-gutters>
                  <v-icon size="60" class="mt-2">mdi-arrow-left</v-icon>
                </v-row>

                <v-card-text>
                  <v-row justify="center" no-gutters>
                    <h4 class="body-2">Back</h4>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col
              sm="1"
              v-for="(item, index) in mediaManager.directories"
              :key="item.name + '-' + index"
              v-show="item.name != 'attachments'"
            >
              <v-card
                @click="selectItem(item)"
                @dblclick="enterDirectory(item.name)"
                height="100%"
                :class="item == selectedItem ? 'grey lighten-3' : ''"
              >
                <v-row justify="center" no-gutters>
                  <v-icon size="60" class="mt-2">mdi-folder</v-icon>
                </v-row>

                <v-card-text>
                  <v-row justify="center" no-gutters>
                    <h4 class="body-2">{{ truncateString(item.name, 8) }}</h4>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col
              sm="1"
              v-for="(item, index) in mediaManager.files.filter(
                v => allowedTypes.indexOf(v.type) !== -1
              )"
              :key="item.name + '-' + index"
            >
              <v-card
                @click="selectItem(item)"
                @dblclick="insertItem(item)"
                height="100%"
                :class="item == selectedItem ? 'grey lighten-3' : ''"
              >
                <v-row justify="center" no-gutters>
                  <v-img
                    v-if="item.type == 'image'"
                    :src="loadMedia(mediaPath + currentPath + item.name, 'xs')"
                    :lazy-src="
                      loadMedia(
                        mediaPath + currentPath + item.name,
                        'thumbnail'
                      )
                    "
                    :aspect-ratio="16 / 9"
                    class="checkered-background"
                  ></v-img>

                  <v-icon
                    v-else-if="item.type == 'video'"
                    size="60"
                    class="mt-2"
                    >mdi-play-box-outline</v-icon
                  >
                  <v-icon v-else size="60" class="mt-2"
                    >mdi-file-document-outline</v-icon
                  >
                </v-row>

                <v-card-text>
                  <v-row justify="center" no-gutters>
                    <h4 class="body-2">{{ truncateString(item.name, 8) }}</h4>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-col>

        <v-col sm="3" md="2" v-if="selectedItem">
          <v-row dense>
            <v-col>
              <media-directory-preview
                v-if="selectedItem.type == 'directory'"
                :directory="selectedItem"
                @deleted="refetch"
              ></media-directory-preview>

              <media-file-preview
                v-else
                :mediaPath="mediaPath"
                :file="selectedItem"
                @deleted="refetch"
                :isDialog="isDialog"
              ></media-file-preview>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card-text>

    <v-divider v-if="isDialog"></v-divider>

    <v-card-actions v-if="isDialog">
      <v-spacer></v-spacer>
      <v-btn
        text
        color="success"
        :disabled="!selectedItem || selectedItem.type == 'directory'"
        @click="insertItem(selectedItem)"
        >insert</v-btn
      >
      <v-btn text color="error" @click="close">close</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import UploadFiles from './UploadFiles'
import CreateDirectory from './CreateDirectory'
import MediaFilePreview from './MediaFilePreview'
import MediaDirectoryPreview from './MediaDirectoryPreview'
import { mediaManager } from '@/graphql/media.gql'

export default {
  name: 'MediaManagerCard',
  props: {
    isDialog: {
      type: Boolean,
      default: false
    },
    allowedTypes: {
      type: Array,
      default() {
        return ['image', 'audio', 'video', 'document']
      }
    }
  },
  components: {
    UploadFiles,
    CreateDirectory,
    MediaFilePreview,
    MediaDirectoryPreview
  },
  data: () => ({
    mediaPath: '/storage/media',
    basePath: '/',
    paths: [],
    selectedItem: null,
    mediaManager: {
      directories: null,
      files: null
    }
  }),
  computed: {
    breadcrumbItems() {
      let breadcrumbs = [
        {
          text: 'media',
          index: null
        }
      ]

      return breadcrumbs.concat(
        this.paths.map((path, index) => {
          return {
            text: path,
            index: index
          }
        })
      )
    },
    currentPath() {
      let path = this.basePath

      if (this.paths.length) {
        path += this.paths.join('/') + '/'
      }

      return path
    }
  },
  apollo: {
    mediaManager: {
      query: mediaManager,
      variables() {
        return {
          path: this.currentPath
        }
      }
    }
  },
  methods: {
    selectItem(item) {
      this.selectedItem = item
    },
    enterDirectory(item) {
      this.selectedItem = null
      this.paths.push(item)
    },
    backDirectory() {
      this.selectedItem = null
      this.paths.pop()
    },
    insertItem(item) {
      if (this.isDialog) {
        this.$emit('insertItem', this.mediaPath + this.currentPath + item.name)
      }
    },
    close() {
      this.$emit('closed')
    },
    navigatePath(index) {
      this.selectedItem = null

      if (index === null) {
        this.paths = []
      } else {
        index = parseFloat(index)

        this.paths.splice(index + 1, this.paths.length)
      }
    },
    refetch() {
      this.selectedItem = null
      this.$apollo.queries.mediaManager.refetch()
    }
  }
}
</script>

<style scoped>
.v-card {
  overflow: hidden;
}
</style>
